import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "toggle",
    "nameField",
    "teaserField",
    "customizeImageControl",
    "previewName",
    "previewTeaser",
    "previewPhoto",
    "previewPhotoEmpty",
    "nameCount",
    "teaserCount",
    "startDate",
    "endDate",
    "totalFigure",
    "totalBreakdown",
    "discountField",
    "discountSection",
    "backfillToggle",
    "calendarSection",
    "dateRangeSelector"
  ]

  static values = {
    adjustedUnitPrice: { type: Number, default: 0 },
    discountPercent: { type: Number, default: 0 },
    uploadedAdImageRequired: { type: Boolean, default: false }
  }

  connect() {
    this.sync()
    this.syncBackfill()
    this.mirrorPreview()
    this.updateTotal()
  }

  disconnect() {
    if (this.calendarRefreshTimer) {
      window.clearTimeout(this.calendarRefreshTimer)
      this.calendarRefreshTimer = null
    }
  }

  toggle() {
    this.sync()
    this.mirrorPreview()
  }

  toggleBackfill() {
    this.syncBackfill()
  }

  syncBackfill() {
    if (!this.hasBackfillToggleTarget) return
    const on = this.backfillToggleTarget.checked
    this.backfillToggleTarget.setAttribute("aria-checked", on ? "true" : "false")
    if (this.hasCalendarSectionTarget) {
      this.calendarSectionTarget.classList.toggle("[grid-template-rows:0fr]", on)
      this.calendarSectionTarget.classList.toggle("[grid-template-rows:1fr]", !on)
    }
    if (on) {
      if (this.hasStartDateTarget) this.startDateTarget.value = ""
      if (this.hasEndDateTarget) this.endDateTarget.value = ""
    }
    if (this.hasDiscountSectionTarget) {
      this.discountSectionTarget.classList.toggle("hidden", on)
    }
    this.refreshDateRangeSelectorLayout()
    this.updateTotal()
  }

  refreshDateRangeSelectorLayout() {
    const run = () => {
      if (!this.hasDateRangeSelectorTarget) return
      this.dateRangeSelectorTarget.dispatchEvent(
        new CustomEvent("date-range-selector:sync", { bubbles: false })
      )
    }
    // After current Stimulus connect pass; date-range-selector may not exist on the first microtask.
    window.setTimeout(run, 0)
    if (this.calendarRefreshTimer) window.clearTimeout(this.calendarRefreshTimer)
    // Match ad form calendar grid transition (300ms) so day cells re-sync after expand.
    this.calendarRefreshTimer = window.setTimeout(run, 340)
  }

  onImageReady(event) {
    const url = event.detail?.url
    if (!url) return
    this.previewPhotoTargets.forEach((img) => {
      img.src = url
      img.classList.remove("opacity-0")
      img.classList.remove("object-contain")
      img.classList.add("object-cover")
    })
    this.previewPhotoEmptyTargets.forEach((el) => el.classList.add("hidden"))
  }

  onImageCleared() {
    this.previewPhotoTargets.forEach((img) => {
      const d = img.dataset.defaultSrc
      if (d) {
        img.src = d
        img.classList.remove("opacity-0")
        img.classList.toggle("object-contain", img.dataset.defaultObjectFit === "object-contain")
        img.classList.toggle("object-cover", img.dataset.defaultObjectFit !== "object-contain")
      } else {
        img.src =
          "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
        img.classList.add("opacity-0")
      }
    })
    const hasDefault = this.previewPhotoTargets.some((img) => img.dataset.defaultSrc)
    const hideImageRequiredMessage = hasDefault && !this.uploadedAdImageRequiredValue
    this.previewPhotoEmptyTargets.forEach((el) => el.classList.toggle("hidden", hideImageRequiredMessage))
    this.mirrorPreview()
  }

  updateTotal() {
    if (!this.hasTotalFigureTarget) return
    if (this.hasBackfillToggleTarget && this.backfillToggleTarget.checked) {
      this.totalFigureTarget.textContent = this.formatCurrency(0)
      this.renderBreakdown(0)
      return
    }
    if (!this.hasStartDateTarget || !this.hasEndDateTarget) return

    const days = this.inclusiveDayCount(this.startDateTarget.value, this.endDateTarget.value)
    const discount = this.discountFromInputs()
    const rate = Number(this.adjustedUnitPriceValue) || 0
    const raw = days > 0 ? rate * days * (1 - discount / 100) : 0
    const rounded = Math.round(raw * 100) / 100

    this.totalFigureTarget.textContent = this.formatCurrency(rounded)
    this.renderBreakdown(days)
  }

  renderBreakdown(days) {
    if (!this.hasTotalBreakdownTarget) return

    const rate = Number(this.adjustedUnitPriceValue) || 0
    const dayLabel = days === 1 ? "day" : "days"
    this.totalBreakdownTarget.textContent = `${this.formatCurrency(rate)} × ${days} ${dayLabel}`
  }

  discountFromInputs() {
    let n = 0
    if (this.hasDiscountFieldTarget) {
      const v = Number(this.discountFieldTarget.value)
      n = Number.isFinite(v) ? Math.trunc(v) : 0
    } else {
      n = Math.trunc(Number(this.discountPercentValue) || 0)
    }
    return Math.min(100, Math.max(0, n))
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  }

  inclusiveDayCount(startStr, endStr) {
    if (!startStr || !endStr) return 0
    const startParts = startStr.split("-").map((part) => Number.parseInt(part, 10))
    const endParts = endStr.split("-").map((part) => Number.parseInt(part, 10))
    if (startParts.length !== 3 || endParts.length !== 3) return 0
    if (startParts.some((n) => Number.isNaN(n)) || endParts.some((n) => Number.isNaN(n))) return 0

    const start = new Date(startParts[0], startParts[1] - 1, startParts[2])
    const end = new Date(endParts[0], endParts[1] - 1, endParts[2])
    if (end < start) return 0

    let days = 0
    const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate())
    while (cursor <= end) {
      days += 1
      cursor.setDate(cursor.getDate() + 1)
    }
    return days
  }

  mirrorPreview() {
    if (this.hasNameFieldTarget) {
      const name = this.nameFieldTarget.value
      this.previewNameTargets.forEach((el) => {
        el.textContent = name
        el.classList.toggle("hidden", name.length === 0)
      })
      this.nameCountTargets.forEach((el) => { el.textContent = String(name.length) })
    }

    if (this.hasTeaserFieldTarget) {
      const teaser = this.teaserFieldTarget.value
      this.previewTeaserTargets.forEach((el) => {
        el.textContent = teaser
        el.classList.toggle("hidden", teaser.length === 0)
      })
      this.teaserCountTargets.forEach((el) => { el.textContent = String(teaser.length) })
    }
  }

  sync() {
    if (!this.hasToggleTarget) return

    const on = this.toggleTarget.checked
    this.toggleTarget.setAttribute("aria-checked", on ? "true" : "false")

    if (this.hasNameFieldTarget) {
      this.applyFieldMode(this.nameFieldTarget, on, false)
      this.nameFieldTarget.readOnly = !on
    }
    if (this.hasTeaserFieldTarget) {
      this.applyFieldMode(this.teaserFieldTarget, on, true)
      this.teaserFieldTarget.readOnly = !on
    }

    this.customizeImageControlTargets.forEach((control) => {
      control.classList.toggle("hidden", !on)
    })

  }

  applyFieldMode(el, enabled, isTextarea) {
    const base = isTextarea
      ? "mt-2 block w-full resize-y rounded-md border px-3 py-2 text-sm shadow-none focus:outline-none focus:ring-0"
      : "mt-2 block w-full rounded-md border px-3 py-2 text-sm shadow-none focus:outline-none focus:ring-0"
    if (enabled) {
      el.className = `${base} border-gray-300 bg-white text-gray-900 focus:border-gray-900`
    } else {
      el.className = `${base} cursor-not-allowed border-gray-200 bg-gray-50 text-gray-700`
    }
  }

}
